<script>
    import { Meteor } from "meteor/meteor";
    import { Tracker } from "meteor/tracker";
    import { onMount } from "svelte";
    import { FlowRouter } from "meteor/ostrio:flow-router-extra";
    import { InventoryCollection } from "../../api/inventory/inventory";

    let { vehicleId = "" } = $props();

    let ready = $state(false);
    let vehicle = $state(null);
    let selectedMediaKey = $state("");

    const fallbackImage = "/vehicles/cars.webp";

    function currency(value) {
        return new Intl.NumberFormat("en-CA", {
            style: "currency",
            currency: "CAD",
            maximumFractionDigits: 0,
        }).format(Number(value || 0));
    }

    function toPlainText(value) {
        return String(value || "")
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim();
    }

    const title = $derived(
        vehicle?.title ||
            [vehicle?.year, vehicle?.make, vehicle?.model]
                .filter(Boolean)
                .join(" ") ||
            "Vehicle",
    );
    const descriptionText = $derived(toPlainText(vehicle?.description));
    const photos = $derived(
        Array.isArray(vehicle?.photos) ? vehicle.photos : [],
    );
    const videos = $derived(
        Array.isArray(vehicle?.videos) ? vehicle.videos : [],
    );
    const galleryItems = $derived([
        ...photos.map((photo) => ({
            key: `photo:${photo.id}`,
            type: "photo",
            thumb:
                photo?.variants?.thumb ||
                photo?.variants?.card ||
                photo?.original ||
                fallbackImage,
            src:
                photo?.variants?.detail ||
                photo?.variants?.card ||
                photo?.original ||
                fallbackImage,
        })),
        ...videos.map((video) => ({
            key: `video:${video.id}`,
            type: "video",
            source: video?.source || "upload",
            thumb: video?.poster || fallbackImage,
            src:
                video?.outputs?.hd ||
                video?.outputs?.sd ||
                video?.original ||
                "",
            embedUrl: video?.embedUrl || "",
        })),
    ]);

    const activeMediaKey = $derived(
        galleryItems.some((item) => item.key === selectedMediaKey)
            ? selectedMediaKey
            : galleryItems[0]?.key || "",
    );
    const activeMedia = $derived(
        galleryItems.find((item) => item.key === activeMediaKey) || null,
    );

    const specRows = $derived(
        [
            ["Type", vehicle?.type],
            ["Year", vehicle?.year],
            ["Make", vehicle?.make],
            ["Model", vehicle?.model],
            [
                "Mileage",
                vehicle?.mileage
                    ? `${Number(vehicle.mileage).toLocaleString()} km`
                    : null,
            ],
            ["Color", vehicle?.color],
            ["Drive", vehicle?.drive],
            ["Transmission", vehicle?.transmission],
            ["Trim", vehicle?.trim],
            ["Engine", vehicle?.engine],
            ["Interior", vehicle?.interiorColor],
            ["Upholstery", vehicle?.upholstery],
            ["Condition", vehicle?.condition],
            ["VIN", vehicle?.vin],
        ].filter(
            ([, value]) =>
                value !== null &&
                value !== undefined &&
                String(value).trim() !== "",
        ),
    );

    onMount(() => {
        if (!vehicleId) {
            ready = true;
            vehicle = null;
            return undefined;
        }

        const handle = Meteor.subscribe("inventory.publicById", vehicleId);
        const computation = Tracker.autorun(() => {
            ready = handle.ready();
            vehicle = InventoryCollection.findOne({ _id: vehicleId }) || null;
        });

        return () => {
            computation.stop();
            handle.stop();
        };
    });
</script>

<section class="vehicle-detail">
    {#if !ready}
        <p>Loading vehicle details...</p>
    {:else if !vehicle}
        <h1>Vehicle Not Found</h1>
        <p>This listing may have been removed or is no longer available.</p>
        <button
            type="button"
            class="secondary-btn"
            onclick={() => FlowRouter.go("/vehicles")}>Back to Search</button
        >
    {:else}
        <div class="vehicle-detail-top">
            <div>
                <p class="eyebrow">Vehicle Details</p>
                <h1>{title}</h1>
                {#if vehicle?.price !== undefined && vehicle?.price !== null && vehicle?.price !== ""}
                    <p class="vehicle-detail-price">
                        {currency(vehicle.price)}
                    </p>
                {/if}
            </div>
            <div class="vehicle-detail-cta">
                <button
                    type="button"
                    class="secondary-btn"
                    onclick={() => FlowRouter.go("/vehicles")}
                    >Back to Search</button
                >
                <button
                    type="button"
                    onclick={() => FlowRouter.go("/application")}
                    >Apply for Financing</button
                >
            </div>
        </div>

        <div class="vehicle-detail-grid">
            <div class="vehicle-media">
                <div class="vehicle-main-media">
                    {#if activeMedia?.type === "video" && activeMedia?.source === "youtube" && activeMedia?.embedUrl}
                        <iframe
                            src={activeMedia.embedUrl}
                            title="Vehicle video"
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowfullscreen
                        ></iframe>
                    {:else if activeMedia?.type === "video" && activeMedia?.src}
                        <video
                            controls
                            preload="metadata"
                            src={activeMedia.src}
                        >
                            <track kind="captions" />
                        </video>
                    {:else}
                        <img
                            src={activeMedia?.src || fallbackImage}
                            alt={title}
                        />
                    {/if}
                </div>

                {#if galleryItems.length > 1}
                    <div class="vehicle-thumb-grid">
                        {#each galleryItems as media (media.key)}
                            <button
                                type="button"
                                class="vehicle-thumb"
                                class:active={activeMediaKey === media.key}
                                onclick={() => (selectedMediaKey = media.key)}
                                title={media.type === "photo"
                                    ? "Photo"
                                    : "Video"}
                            >
                                <img
                                    src={media.thumb || fallbackImage}
                                    alt={media.type === "photo"
                                        ? "Vehicle photo"
                                        : "Vehicle video"}
                                />
                                <span
                                    >{media.type === "photo"
                                        ? "Photo"
                                        : "Video"}</span
                                >
                            </button>
                        {/each}
                    </div>
                {/if}
            </div>

            <aside class="vehicle-specs">
                <h2>Specs</h2>
                {#if specRows.length === 0}
                    <p>Specs will be added shortly.</p>
                {:else}
                    <dl>
                        {#each specRows as [label, value] (label)}
                            <div>
                                <dt>{label}</dt>
                                <dd>{value}</dd>
                            </div>
                        {/each}
                    </dl>
                {/if}
            </aside>
        </div>

        {#if descriptionText}
            <section class="vehicle-description">
                <h2>Description</h2>
                <p class="vehicle-description-copy">{descriptionText}</p>
            </section>
        {/if}
    {/if}
</section>
